import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { supabase, getMyProfile, canAccess, hasFullAccess, isManager } from './supabase';
import Auth from './Auth';
import Dashboard from './Dashboard';
import DispatchForm from './DispatchForm';
import AcademyForm from './AcademyForm';
import TechForms from './TechForms';
import Matrix from './Matrix';
import { ContactsPage, CompaniesPage } from './ContactsCompanies';
import ReferralsPage from './Referrals';
import QBOConnect from './QBOConnect';
import PropertiesPage from './PropertiesPage';
import FinancialsPage from './FinancialsPage';
import ClientInvoicesPage from './ClientInvoices';
import RoyaltiesPage from './Royalties';
import ParticipationPortal from './Participation';
import JurisdictionsPage from './Jurisdictions';
import IntercompanyPage from './Intercompany';

function NavItem({ label, active, onClick, dot }) {
  return (
    <div onClick={onClick} className={"sidebar-item" + (active ? " active" : "")}>
      <div className={"sidebar-dot dot-" + (dot || 'gray')} />
      {label}
    </div>
  );
}

function AppShell({ session, profile, signOut }) {
  const navigate = useNavigate();
  const location = useLocation();

  const view = location.pathname.replace('/', '') || 'dashboard';
  const manager = isManager(profile);

  // Registered royalty participants (e.g. Wesley) get a read-only "My participation" page.
  const [participant, setParticipant] = useState(false);
  useEffect(() => {
    if (!profile || profile.role === 'admin') return;
    supabase.from('royalty_participants').select('profile_id').eq('profile_id', profile.id).maybeSingle()
      .then(({ data }) => setParticipant(!!data), () => setParticipant(false));
  }, [profile]);

  useEffect(() => {
    if (profile && profile.role === 'input' && location.pathname === '/') {
      if (canAccess(profile, 'national') || canAccess(profile, 'florida') || canAccess(profile, 'newjersey')) navigate('/dispatch', { replace: true });
      else if (canAccess(profile, 'academy')) navigate('/academy', { replace: true });
      else if (canAccess(profile, 'technologies')) navigate('/technologies', { replace: true });
    }
  }, [profile, location.pathname, navigate]);

  const navSections = [];
  if (participant) navSections.push({ label: 'MY ROYALTIES', items: [{ id: 'participation', label: 'My participation', dot: 'green' }] });
  if (manager) {
    navSections.push({ label: 'OVERVIEW', items: [
      { id: 'dashboard', label: 'Dashboard', dot: 'red' },
      { id: 'matrix', label: 'Market matrix', dot: 'amber' },
    ]});
  }

  const intakeItems = [];
  if (canAccess(profile, 'national') || canAccess(profile, 'florida') || canAccess(profile, 'newjersey')) intakeItems.push({ id: 'dispatch', label: 'Dispatch intake', dot: 'red' });
  if (canAccess(profile, 'academy')) intakeItems.push({ id: 'academy', label: 'Academy enrollment', dot: 'green' });
  if (canAccess(profile, 'technologies')) intakeItems.push({ id: 'technologies', label: 'Technologies', dot: 'blue' });
  if (intakeItems.length) navSections.push({ label: 'INTAKE', items: intakeItems });

  if (manager) {
    navSections.push({ label: 'DATABASE', items: [
      { id: 'properties', label: 'Properties', dot: 'blue' },
      { id: 'contacts', label: 'Contacts', dot: 'purple' },
      { id: 'companies', label: 'Companies', dot: 'gray' },
      { id: 'referrals', label: 'Referral partners', dot: 'green' },
      { id: 'jurisdictions', label: 'Jurisdictions', dot: 'red' },
      { id: 'qbo', label: 'QuickBooks', dot: 'blue' },
    ]});
    const intelItems = [{ id: 'financials', label: 'Financials', dot: 'green' }, { id: 'client-invoices', label: 'Client invoices', dot: 'blue' }];
    if (profile.role === 'admin') {
      intelItems.push({ id: 'royalties', label: 'Royalties', dot: 'red' });
      intelItems.push({ id: 'intercompany', label: 'Intercompany', dot: 'amber' });
    }
    navSections.push({ label: 'INTELLIGENCE', items: intelItems });
  }

  const PAGE_TITLES = {
    dashboard: 'Portfolio overview',
    matrix: 'Market prioritization matrix',
    dispatch: 'Dispatch intake',
    academy: 'Academy enrollment intake',
    technologies: 'Technologies intake',
    contacts: 'Contact database',
    companies: 'Company database',
    referrals: 'Referral partner registry',
    qbo: 'QuickBooks connection',
    properties: 'Property map',
    financials: 'Financial dashboard',
    jurisdictions: 'Fire marshal & jurisdiction tracker',
    intercompany: 'Intercompany ledger',
    'client-invoices': 'Client invoices & collections',
    royalties: 'Royalties',
    participation: 'My participation',
  };

  return (
    <div className="app-shell">
      <div className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-name">AΩ</div>
          <div className="sidebar-brand-sub">AO Intel</div>
        </div>
        <nav className="sidebar-nav">
          {navSections.map(section => (
            <div key={section.label}>
              <div className="sidebar-section-label">{section.label}</div>
              {section.items.map(item => (
                <NavItem
                  key={item.id}
                  label={item.label}
                  dot={item.dot}
                  active={view === item.id}
                  onClick={() => navigate('/' + item.id)}
                />
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user-name">{profile.full_name || profile.email}</div>
          <div className="sidebar-user-role">{profile.role}</div>
          <div className="sidebar-entity-tags">
            {profile.entities && profile.entities.map(e => (
              <span key={e.entity} className="sidebar-entity-tag">{e.entity}{e.access_level === 'input' ? ' (in)' : ''}</span>
            ))}
          </div>
          <button onClick={signOut} className="sidebar-signout">Sign out</button>
        </div>
      </div>

      <div className="main-area">
        <div className="topbar">
          <span className="topbar-title">{PAGE_TITLES[view] || view}</span>
          <span className="topbar-date">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <div className="page-content">
          <Routes>
            <Route path="/" element={manager ? <Dashboard profile={profile} onNavigate={(v) => navigate('/' + v)} /> : null} />
            <Route path="/dashboard" element={manager ? <Dashboard profile={profile} onNavigate={(v) => navigate('/' + v)} /> : <Navigate to="/" replace />} />
            <Route path="/matrix" element={manager ? <Matrix /> : <Navigate to="/" replace />} />
            <Route path="/dispatch" element={<DispatchForm profile={profile} />} />
            <Route path="/academy" element={<AcademyForm profile={profile} hasFullAccess={hasFullAccess(profile, 'academy')} />} />
            <Route path="/technologies" element={<TechForms profile={profile} hasFullAccess={hasFullAccess(profile, 'technologies')} />} />
            <Route path="/contacts" element={manager ? <ContactsPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/companies" element={manager ? <CompaniesPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/referrals" element={manager ? <ReferralsPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/qbo" element={profile.role === 'admin' ? <QBOConnect /> : <Navigate to="/" replace />} />
            <Route path="/properties" element={manager ? <PropertiesPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/financials" element={manager ? <FinancialsPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/jurisdictions" element={manager ? <JurisdictionsPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/client-invoices" element={manager ? <ClientInvoicesPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/participation" element={participant ? <ParticipationPortal profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/royalties" element={profile.role === 'admin' ? <RoyaltiesPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="/intercompany" element={profile.role === 'admin' ? <IntercompanyPage profile={profile} /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadProfile(); else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadProfile(); else { setProfile(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile() {
    setLoading(true);
    const p = await getMyProfile();
    setProfile(p);
    setLoading(false);
  }

  async function signOut() { await supabase.auth.signOut(); }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font)', color: '#94A3B8', fontSize: '14px', background: '#F8F9FA' }}>
      Loading…
    </div>
  );

  if (!session) return <Auth />;

  if (!profile) return (
    <div className="pending-page">
      <div className="pending-card">
        <div className="pending-icon">⏳</div>
        <div className="pending-title">Access pending</div>
        <div className="pending-desc">Your account hasn't been provisioned yet. Contact Jacob to get your access assigned.</div>
        <button onClick={signOut} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>Sign out</button>
      </div>
    </div>
  );

  return (
    <BrowserRouter>
      <AppShell session={session} profile={profile} signOut={signOut} />
    </BrowserRouter>
  );
}